import { motion } from 'framer-motion';
import { Instagram, Facebook, Linkedin, Mail, Globe, MapPin, ArrowUpRight } from 'lucide-react';

// Custom TikTok icon component
const TikTok = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/kingdomvibestudios?igsh=anB2OHRrMm1hazl5&utm_source=qr', label: 'Instagram', color: 'hover:text-pink-500' },
    { icon: Facebook, href: 'https://www.facebook.com/share/1AvPsnck2i/?mibextid=wwXIfr', label: 'Facebook', color: 'hover:text-blue-500' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/brian-b-b6077a3ab?utm_source=share_via&utm_content=profile&utm_medium=member_ios', label: 'LinkedIn', color: 'hover:text-blue-400' },
    { icon: TikTok, href: 'https://www.tiktok.com/@kingdom.v.i.b.e.s?_r=1&_t=ZT-93nGLpPmrgT', label: 'TikTok', color: 'hover:text-white' },
  ];

  const quickLinks = [
    { label: 'The Problem', href: '#problem-solution' },
    { label: 'V.I.B.E.', href: '#vibe' },
    { label: 'The Solution', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Our Process', href: '#pricing' },
    { label: 'Results', href: '#testimonials' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-obsidian border-t border-white/5" data-testid="footer-section">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-12 md:py-16 lg:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-8">
          {/* Quick Links - Left */}
          <div className="text-center sm:text-left">
            <h3 className="font-heading text-base sm:text-lg font-bold uppercase tracking-wider mb-4 sm:mb-6 text-white">
              Quick Links
            </h3>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-sm sm:text-base text-white/50 hover:text-neon-blue transition-colors flex items-center justify-center sm:justify-start gap-2 group"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    <span>{link.label}</span>
                    <ArrowUpRight size={12} className="sm:w-3.5 sm:h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Middle */}
          <div className="text-center sm:text-left">
            <h3 className="font-heading text-base sm:text-lg font-bold uppercase tracking-wider mb-4 sm:mb-6 text-white">
              Contact
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <a href="mailto:info@kingdomvibestudios.com" className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3 text-sm sm:text-base text-white/50 hover:text-neon-pink transition-colors">
                  <Mail size={16} className="sm:w-[18px] sm:h-[18px] text-neon-pink" />
                  <span className="break-all">info@kingdomvibestudios.com</span>
                </a>
              </li>
              <li className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3 text-sm sm:text-base text-white/50">
                <Globe size={16} className="sm:w-[18px] sm:h-[18px] text-neon-blue" />
                <span>Serving Clients Nationwide</span>
              </li>
              <li className="flex items-start justify-center sm:justify-start gap-2 sm:gap-3 text-sm sm:text-base text-white/50">
                <MapPin size={16} className="sm:w-[18px] sm:h-[18px] text-amber-400 flex-shrink-0 mt-0.5" />
                <span>Summerville, SC</span>
              </li>
            </ul>

            {/* Social Links */}
            <div className="mt-6 sm:mt-8">
              <h4 className="text-xs sm:text-sm text-white/30 uppercase tracking-wider mb-3 sm:mb-4">Follow Us</h4>
              <div className="flex justify-center sm:justify-start gap-3 sm:gap-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-9 h-9 sm:w-10 sm:h-10 border border-white/10 flex items-center justify-center text-white/50 ${social.color} hover:border-current transition-all`}
                    aria-label={social.label}
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                  >
                    <social.icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Brand Column - Right */}
          <div className="flex flex-col items-center text-center sm:col-span-2 lg:col-span-1">
            <img
              src="https://customer-assets.emergentagent.com/job_faith-creators/artifacts/xdjcrr32_Crown.png"
              alt="Kingdom V.I.B.E. Studios"
              className="h-20 sm:h-24 md:h-28 w-auto mb-2 sm:mb-3"
              data-testid="footer-logo"
            />
            <p className="text-white/50 leading-relaxed mb-2 sm:mb-3 max-w-sm text-xs sm:text-sm">
              We handle all the tech stress so you can focus on delivering your message.
            </p>
            <p className="font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase text-neon-blue neon-text-blue">
              The Experience Upgrade
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <p className="text-xs sm:text-sm text-white/30 text-center sm:text-left">
            © {currentYear} Kingdom V.I.B.E. Studios. All rights reserved.
          </p>
          <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-white/30">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>

      {/* Back to Top */}
      <motion.button
        onClick={scrollToTop}
        whileHover={{ y: -5 }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 w-10 h-10 sm:w-12 sm:h-12 bg-amber-400 text-obsidian flex items-center justify-center hover:shadow-[0_0_20px_rgba(251,191,36,0.5)] transition-shadow z-50"
        data-testid="back-to-top"
      >
        <ArrowUpRight size={16} className="sm:w-5 sm:h-5" />
      </motion.button>
    </footer>
  );
};

export default Footer;
